import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  type PublishedLandingPage,
  getLandingPageBySlug,
  getGeoJsonPlacesByLandingPageSlug,
  type PlaceFeature,
  type PlacePreviewFeature,
  type RelatedPlace,
  relatedPlaceToPreview,
} from "../lib/api";
import { buildLandingPageJsonLd } from "../lib/jsonLd";
import { emptyPlacesGeoJSON } from "../lib/mapConstants";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { usePlaceNavigation } from "../hooks/usePlaceNavigation";
import { Card } from "../components/ui/card";
import { PageHead } from "../components/PageHead";
import { LandingNotFound } from "../components/landing/LandingNotFound";
import MobileLandingPage from "./MobileLandingPage";
import DesktopLandingPage from "./DesktopLandingPage";

interface LandingPageProps {
  slug?: string;
}

export default function LandingPage({ slug: propSlug }: LandingPageProps) {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = propSlug ?? paramSlug;
  const navigate = useNavigate();
  const { below } = useBreakpoint();
  const isMobile = below("md");
  const instrumentation = useInstrumentation();

  const [mapData, setMapData] = useState<PublishedLandingPage | null>(null);
  const [places, setPlaces] = useState(emptyPlacesGeoJSON);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);

  // Navigation callback: handles place ID (from URL) or preview feature (from history state)
  const handlePlaceChange = useCallback(
    (data: string | PlacePreviewFeature | null) => {
      if (!data) {
        setSelectedPlace(null);
        return;
      }

      // Preview feature from history state - use directly
      if (typeof data !== "string") {
        setSelectedPlace(data as PlaceFeature);
        return;
      }

      // Place ID from URL - look up the feature
      const place = places.features.find((f) => f.id === data);
      setSelectedPlace(place ?? null);
    },
    [places.features],
  );

  const { navigateToPlace, clearPlaceSelection, originPlaceId } = usePlaceNavigation({
    selectedPlaceId: selectedPlace?.properties.id ?? null,
    onPlaceChange: handlePlaceChange,
  });

  const handlePlaceSelect = useCallback(
    (placeId: string) => {
      const place = places.features.find((f) => f.id === placeId);
      if (place && mapData) {
        instrumentation.track("Place Selected", {
          landingPage: mapData.slug,
          placeId,
          placeType: place.properties.type,
          context: "landing_page",
        });
      }
      if (place) navigateToPlace(place);
    },
    [places.features, mapData, instrumentation, navigateToPlace],
  );

  const handleBack = useCallback(() => {
    if (originPlaceId) {
      navigate(-1);
    } else {
      clearPlaceSelection();
    }
  }, [originPlaceId, navigate, clearPlaceSelection]);

  const handleSelectRelatedPlace = useCallback(
    (relatedPlace: RelatedPlace) => {
      const feature = places.features.find((f) => f.id === relatedPlace.id) ?? relatedPlaceToPreview(relatedPlace);
      navigateToPlace(feature, { originPlaceId: selectedPlace?.properties.id, preview: true });
    },
    [places.features, navigateToPlace, selectedPlace],
  );

  // Re-sync URL → state when places load (URL may have ?place=id before places were available)
  const [searchParams] = useSearchParams();
  const urlPlaceId = searchParams.get("place");
  useEffect(() => {
    if (!urlPlaceId || selectedPlace) return;
    handlePlaceChange(urlPlaceId);
  }, [urlPlaceId, selectedPlace, places.features, handlePlaceChange]);

  useEffect(() => {
    if (!slug) return;

    setLoading(true);
    Promise.all([getLandingPageBySlug(slug), getGeoJsonPlacesByLandingPageSlug(slug)])
      .then(([mapData, placesData]) => {
        if (!mapData.publishedAt) {
          throw new Error("Not published");
        }
        setMapData(mapData);
        setPlaces(placesData);
        instrumentation.track("Landing Page Viewed", {
          slug: mapData.slug,
          placeCount: placesData.features.length,
        });
      })
      .catch((err) => {
        console.error(err);
        setError("Map not found");
      })
      .finally(() => setLoading(false));
  }, [slug, instrumentation]);

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-4 bg-background">
        <Card className="p-8 text-center text-muted-foreground">Loading map...</Card>
      </div>
    );
  }

  if (error || !mapData) {
    return <LandingNotFound />;
  }

  // SEO data - includes selected place for deep-linking
  const canonicalUrl = mapData.slug ? `https://${mapData.slug}.noma.to/` : undefined;
  const pageTitle = selectedPlace ? `${selectedPlace.properties.name} - ${mapData.name}` : mapData.name;
  const pageDescription =
    selectedPlace?.properties.description ||
    mapData.description ||
    `Explore ${mapData.name} - curated outdoor adventures`;
  const firstImagePlace = places.features.find((f) => f.properties.images.length > 0);
  const ogImage = selectedPlace?.properties.images[0]?.large || firstImagePlace?.properties.images[0]?.large;
  const effectiveCanonical = selectedPlace && canonicalUrl ? `${canonicalUrl}?place=${selectedPlace.id}` : canonicalUrl;
  const jsonLd = canonicalUrl ? buildLandingPageJsonLd({ map: mapData, places, canonicalUrl }) : undefined;

  const PageContent = isMobile ? MobileLandingPage : DesktopLandingPage;

  return (
    <>
      <PageHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={effectiveCanonical}
        image={ogImage}
        author={mapData.curatedBy?.displayName}
        publishedAt={mapData.publishedAt}
        updatedAt={mapData.updatedAt}
        jsonLd={jsonLd}
      />
      <PageContent
        landingPage={mapData}
        places={places}
        selectedPlace={selectedPlace}
        onPlaceSelect={handlePlaceSelect}
        onPlaceDeselect={handleBack}
        onSelectRelatedPlace={handleSelectRelatedPlace}
      />
    </>
  );
}
